import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Users } from "lucide-react";

const keyOf = (platform, gamertag) => `${platform}:${(gamertag || "").toLowerCase()}`;
const statusColor = (s) => (s === "confirmed_cheater" || s === "banned" ? "#FF2A55" : s === "suspect" || s === "disputed" ? "#F97316" : "#94A3B8");

// A small "crew" graph: the focus player in the center, teammates (shared-clip links) around them.
export const CrewMap = ({ reportId, onNavigate }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get(`/reports/${reportId}/crew`).then((r) => alive && setData(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [reportId]);

  if (!data || (data.nodes || []).length < 2) return null;

  const W = 320, H = 210, cx = W / 2, cy = H / 2, R = 78;
  const focus = data.nodes.find((n) => n.is_focus) || data.nodes[0];
  const outer = data.nodes.filter((n) => n !== focus);
  const pos = { [keyOf(focus.platform, focus.gamertag)]: { x: cx, y: cy } };
  outer.forEach((n, i) => {
    const a = (2 * Math.PI * i) / outer.length - Math.PI / 2;
    pos[keyOf(n.platform, n.gamertag)] = { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  });

  const go = (n) => { onNavigate && onNavigate(); navigate(`/players/${encodeURIComponent(n.platform)}/${encodeURIComponent(n.gamertag)}`); };

  return (
    <div className="mt-4 rounded-lg border border-[#7C3AED]/25 bg-[#7C3AED]/[0.06] p-3" data-testid="crew-map">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#C084FC]">
        <Users size={13} /> The Crew <span className="font-normal normal-case text-[#94A3B8]">· {data.nodes.length} accounts caught playing together</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" style={{ maxHeight: 240 }} role="img" aria-label="Crew map">
        {data.edges.map((e, i) => {
          const a = pos[keyOf(e.ap, e.a)], b = pos[keyOf(e.bp, e.b)];
          if (!a || !b) return null;
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#7C3AED" strokeOpacity="0.4" strokeWidth="1.5" />;
        })}
        {data.nodes.map((n, i) => {
          const p = pos[keyOf(n.platform, n.gamertag)];
          if (!p) return null;
          const c = statusColor(n.status);
          const label = n.gamertag.length > 12 ? n.gamertag.slice(0, 11) + "…" : n.gamertag;
          return (
            <g key={i} onClick={() => go(n)} style={{ cursor: "pointer" }} data-testid={`crew-node-${i}`}>
              <circle cx={p.x} cy={p.y} r={n.is_focus ? 9 : 7} fill={c} stroke={n.is_focus ? "#fff" : "#0F1424"} strokeWidth={n.is_focus ? 2 : 1.5} />
              <text x={p.x} y={p.y + (p.y > cy ? 20 : -12)} textAnchor="middle" fontSize="10" fontWeight={n.is_focus ? "700" : "500"} fill="#E2E8F0">{label}</text>
            </g>
          );
        })}
      </svg>
      <p className="text-[11px] text-[#64748B]">Tap a player to open their case. Red = confirmed/banned · orange = suspect.</p>
    </div>
  );
};
